import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { api, ApiError } from '../lib/api'
import { createAdminRequest } from '../lib/adminApi'
import { clearAdminToken, getAdminToken, setAdminToken } from '../lib/storage'
import type { AdminOverview } from '../types/protocol'
import AdminTokenGate from '../components/admin/AdminTokenGate'
import SyncPanel from '../components/admin/SyncPanel'
import PlayerTable from '../components/admin/PlayerTable'
import DraftsTable from '../components/admin/DraftsTable'

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'players', label: 'Players' },
  { id: 'drafts', label: 'Drafts' },
] as const

type TabId = (typeof TABS)[number]['id']
type AuthState = 'checking' | 'ok' | 'prompt' | 'error'

// App-level operator console. Deliberately outside the draft flow: no
// DraftProvider, no cable subscription, nothing that assumes a draft.
export default function Admin() {
  const [token, setToken] = useState(() => getAdminToken() ?? '')
  const [attempt, setAttempt] = useState(0)
  const [auth, setAuth] = useState<AuthState>('checking')
  const [overview, setOverview] = useState<AdminOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabId>('overview')

  // The overview fetch doubles as the token check: a 401 means re-prompt, and
  // with ADMIN_TOKEN unset (dev) the first anonymous call just succeeds.
  useEffect(() => {
    let cancelled = false
    setAuth('checking')
    api<AdminOverview>('/admin/overview', { adminToken: token || undefined })
      .then((data) => {
        if (cancelled) return
        setOverview(data)
        setAuth('ok')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof ApiError && e.status === 401) {
          clearAdminToken()
          setAuth('prompt')
        } else {
          setError(e instanceof ApiError ? e.message : 'Could not reach the server')
          setAuth('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [token, attempt])

  const reload = useCallback(() => setAttempt((count) => count + 1), [])

  const request = useMemo(
    () =>
      createAdminRequest(token, () => {
        clearAdminToken()
        setAuth('prompt')
      }),
    [token],
  )

  function unlock(value: string) {
    setAdminToken(value)
    setToken(value)
    // Bump the attempt counter too, so re-entering the same token retries.
    setAttempt((count) => count + 1)
  }

  if (auth === 'prompt') {
    return <AdminTokenGate onSubmit={unlock} retry={attempt > 0 || token !== ''} />
  }

  if (auth === 'error') {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-ink px-4 text-ink-hi">
        <p className="text-crit">{error}</p>
        <button
          type="button"
          onClick={reload}
          className="rounded border border-line-strong px-4 py-2 text-sm hover:bg-surface-3"
        >
          Try again
        </button>
      </main>
    )
  }

  if (!overview) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ink text-ink-lo">
        Loading…
      </main>
    )
  }

  return (
    <main className="min-h-screen">
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-10">
        <header className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="eyebrow">Operator console</p>
            <h1 className="mt-1 font-display text-4xl leading-none font-bold text-ink-hi">
              Admin
            </h1>
            <p className="mt-2 text-sm text-ink-lo">
              Player database and draft housekeeping for this server.
            </p>
          </div>
          <Link
            to="/"
            className="text-sm text-ink-lo underline-offset-4 hover:text-ink-hi hover:underline"
          >
            Back to the app
          </Link>
        </header>

        <nav className="flex gap-2 border-b border-line">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === id
                  ? 'border-signal text-signal'
                  : 'border-transparent text-ink-mid hover:text-ink-hi'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === 'overview' && (
          <div className="space-y-6">
            <PlayerStats overview={overview} />
            <SyncPanel overview={overview} request={request} onSynced={reload} />
          </div>
        )}
        {tab === 'players' && <PlayerTable request={request} onChanged={reload} />}
        {tab === 'drafts' && <DraftsTable request={request} onChanged={reload} />}
      </div>
    </main>
  )
}

function PlayerStats({ overview }: { overview: AdminOverview }) {
  const { players, drafts } = overview
  const positions = Object.entries(players.by_position).sort(([a], [b]) => a.localeCompare(b))

  return (
    <section className="panel p-6">
      <h2 className="eyebrow">At a glance</h2>
      <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Players" value={players.total} />
        <Stat label="Active" value={players.active} />
        <Stat label="Inactive" value={players.inactive} />
        <Stat label="Locked" value={players.locked} hint="Kept through syncs" />
        <Stat label="Added by hand" value={players.manual} />
        <Stat label="No NFL team" value={players.missing_nfl_team} hint="Active players only" />
        <Stat label="Unranked" value={players.unranked} hint="Active players only" />
        <Stat label="Drafts" value={drafts.total} hint={statusSummary(drafts.by_status)} />
      </dl>

      <p className="mt-4 text-sm text-ink-mid">
        Active pool by position:{' '}
        {positions.length === 0
          ? 'empty — run a sync'
          : positions.map(([position, count]) => `${position} ${count}`).join(' · ')}
      </p>
    </section>
  )
}

function statusSummary(byStatus: Record<string, number>): string {
  const entries = Object.entries(byStatus)
  return entries.length === 0 ? 'none yet' : entries.map(([status, count]) => `${count} ${status}`).join(', ')
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-lg border border-line bg-ink p-3">
      <dt className="text-xs uppercase tracking-wide text-ink-lo">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
      {hint && <p className="mt-1 text-xs text-ink-lo">{hint}</p>}
    </div>
  )
}
